import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Spin, Empty, Progress, Avatar, Tag, Button } from 'antd';
import { TeamOutlined, AimOutlined, CheckCircleOutlined, ClockCircleOutlined, FireOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import AppLayout from '../components/AppLayout';
import { useUser } from '../context/UserContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';

dayjs.extend(relativeTime);

const scoreColor = (s) => s == null ? '#aaa' : s >= 75 ? '#91cc75' : s >= 50 ? '#fac858' : '#ee6666';

// Centred chart wrapper — every chart in the page goes through this
function Chart({ option, height, style }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', ...style }}>
      <ReactECharts option={option} style={{ width: '100%', height }} notMerge />
    </div>
  );
}

// ── Tutor Analytics ───────────────────────────────────────────────────────────
function TutorAnalytics({ user, navigate }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/analytics/tutor').then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

  const { stats = {}, students = [], difficulty = [], activity = [] } = data || {};
  const pending  = students.filter(s => s.pending_review > 0);
  const assigned = students.filter(s => s.pending_review === 0);

  // KPI stats (no avg AI score)
  const kpis = [
    { title: 'Active students',     value: stats.active_students  ?? 0, color: '#5470c6', path: '/subscription' },
    { title: 'Pending reviews',     value: stats.pending_reviews  ?? 0, color: stats.pending_reviews > 0 ? '#ee6666' : '#0a0a0a', path: '/stage-gate' },
    { title: 'Completed this week', value: stats.completed_this_week ?? 0, color: '#91cc75' },
  ];

  // Student completion chart
  const studentChartOpt = students.length === 0 ? null : {
    tooltip: { trigger: 'axis', formatter: p => `${p[0].name}: <b>${p[0].value}%</b>` },
    grid: { left: 110, right: 70, top: 10, bottom: 10 },
    xAxis: { type: 'value', max: 100, axisLabel: { formatter: '{value}%' }, splitLine: { lineStyle: { color: '#f5f5f5' } } },
    yAxis: { type: 'category', data: students.map(s => s.student_name.split(' ')[0]), axisLabel: { fontSize: 11 } },
    series: [{
      type: 'bar', barMaxWidth: 22,
      data: students.map(s => {
        const v = Math.round((s.completed_stages / Math.max(s.total_stages, 1)) * 100);
        return { value: v, itemStyle: { color: v === 100 ? '#91cc75' : v >= 60 ? '#5470c6' : v >= 30 ? '#fac858' : '#ee6666', borderRadius: [0, 6, 6, 0] } };
      }),
      label: { show: true, position: 'right', formatter: '{c}%', fontSize: 11, color: '#555' },
    }],
  };

  // Stage difficulty chart
  const diffChartOpt = difficulty.length === 0 ? null : {
    tooltip: { trigger: 'axis', formatter: p => `${p[0].name}: <b>${p[0].value ?? '—'}% pass rate</b>` },
    grid: { left: 150, right: 70, top: 10, bottom: 10 },
    xAxis: { type: 'value', max: 100, axisLabel: { formatter: '{value}%' }, splitLine: { lineStyle: { color: '#f5f5f5' } } },
    yAxis: { type: 'category', data: difficulty.map(d => d.stage_name), axisLabel: { fontSize: 10, width: 135, overflow: 'truncate' } },
    series: [{
      type: 'bar', barMaxWidth: 18,
      data: difficulty.map(d => ({
        value: d.pass_rate ?? 0,
        itemStyle: { color: (d.pass_rate ?? 0) >= 70 ? '#91cc75' : (d.pass_rate ?? 0) >= 40 ? '#fac858' : '#ee6666', borderRadius: [0, 6, 6, 0] },
      })),
      label: { show: true, position: 'right', formatter: '{c}%', fontSize: 10, color: '#555' },
    }],
  };

  // Activity area chart
  const days = Array.from({ length: 14 }, (_, i) => dayjs().subtract(13 - i, 'day').format('D MMM'));
  const submitted = new Array(14).fill(0);
  const completed = new Array(14).fill(0);
  activity.forEach(a => {
    const ts = a.submitted_at || a.completed_at;
    const idx = days.indexOf(dayjs(ts).format('D MMM'));
    if (idx >= 0) { if (a.stage_status === 'submitted') submitted[idx]++; else completed[idx]++; }
  });
  const activityOpt = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['Submitted', 'Completed'], top: 0, right: 0, textStyle: { fontSize: 12 } },
    grid: { left: 30, right: 10, top: 30, bottom: 20 },
    xAxis: { type: 'category', data: days, axisLabel: { fontSize: 10, interval: 2 } },
    yAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: '#f5f5f5' } } },
    series: [
      { name: 'Submitted', type: 'line', smooth: true, areaStyle: { opacity: 0.15 }, data: submitted, itemStyle: { color: '#fac858' }, lineStyle: { color: '#fac858', width: 2 } },
      { name: 'Completed', type: 'line', smooth: true, areaStyle: { opacity: 0.15 }, data: completed, itemStyle: { color: '#91cc75' }, lineStyle: { color: '#91cc75', width: 2 } },
    ],
  };

  return (
    <>
      {/* KPI row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        {kpis.map(k => (
          <Col xs={8} key={k.title}>
            <Card variant="outlined" style={{ borderColor: '#e8e8e8', cursor: k.path ? 'pointer' : 'default', textAlign: 'center' }}
              onClick={() => k.path && navigate(k.path)}>
              <div style={{ fontSize: 32, fontWeight: 700, color: k.color, lineHeight: 1 }}>{k.value}</div>
              <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{k.title}</div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Charts row */}
      <Row gutter={[20, 20]} style={{ marginBottom: 20 }}>
        <Col xs={24} md={12}>
          <Card variant="outlined" style={{ borderColor: '#e8e8e8' }}
            title={<span style={{ fontWeight: 600 }}>Student Completion</span>}
            extra={<Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/stage-gate')}>Open Roadmap →</Button>}>
            {!studentChartOpt
              ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No students assigned yet" />
              : <Chart option={studentChartOpt} height={Math.max(100, students.length * 38)} />
            }
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card variant="outlined" style={{ borderColor: '#e8e8e8' }}
            title={<span style={{ fontWeight: 600 }}>Stage Pass Rates <span style={{ fontSize: 11, color: '#aaa', fontWeight: 400 }}>hardest first</span></span>}>
            {!diffChartOpt
              ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data yet" />
              : <Chart option={diffChartOpt} height={Math.max(100, difficulty.length * 34)} />
            }
          </Card>
        </Col>
      </Row>

      {/* Activity chart */}
      <Card variant="outlined" style={{ borderColor: '#e8e8e8', marginBottom: 20 }}
        title={<span style={{ fontWeight: 600 }}>Submission Activity — Last 14 days</span>}>
        <Chart option={activityOpt} height={160} />
      </Card>

      {/* Full roster */}
      <Card variant="outlined" style={{ borderColor: '#e8e8e8' }}
        title={<span style={{ fontWeight: 600 }}><TeamOutlined style={{ marginRight: 8 }} />Student Roster</span>}
        extra={<Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/stage-gate')}>Open Roadmap →</Button>}>
        {students.length === 0
          ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No students yet" />
          : [...pending, ...assigned].map(s => {
              const pct = Math.round((s.completed_stages / Math.max(s.total_stages, 1)) * 100);
              return (
                <div key={s.assignment_id} onClick={() => navigate(`/stage-gate?studentId=${s.student_id}`)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0',
                    borderBottom: '1px solid #f5f5f5', cursor: 'pointer' }}>
                  <Avatar size={34} src={s.student_avatar} style={{ background: '#5470c6', fontSize: 12, flexShrink: 0 }}>
                    {!s.student_avatar && s.student_name?.[0]}
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{s.student_name}</span>
                        <span style={{ fontSize: 11, color: '#aaa', marginLeft: 8 }}>{s.subject_name} {s.level}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        {s.pending_review > 0 && <Tag color="orange" style={{ margin: 0 }}>{s.pending_review} to review</Tag>}
                        {s.avg_ai_score != null && <span style={{ fontSize: 12, fontWeight: 600, color: scoreColor(s.avg_ai_score) }}>AI {s.avg_ai_score}%</span>}
                        <span style={{ fontSize: 11, color: '#888' }}>{s.completed_stages}/{s.total_stages}</span>
                      </div>
                    </div>
                    <Progress percent={pct} size="small" strokeColor={pct === 100 ? '#91cc75' : '#5470c6'} showInfo={false} />
                  </div>
                </div>
              );
            })
        }
      </Card>
    </>
  );
}

// ── Student Analytics ─────────────────────────────────────────────────────────
function StudentAnalytics({ user, navigate }) {
  const [assignments, setAssignments] = useState([]);
  const [stages, setStages]           = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/api/gate/assignments/student/${user.userId}`),
      api.get(`/api/analytics/student/${user.userId}`),
    ]).then(([aRes, sRes]) => {
      setAssignments(aRes.data || []);
      setStages(sRes.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin /></div>;

  if (assignments.length === 0) {
    return <Empty description="No assignments yet." style={{ padding: 80 }} />;
  }

  const totalCompleted = assignments.reduce((n, a) => n + (a.completed_stages || 0), 0);
  const totalStages    = assignments.reduce((n, a) => n + (a.total_stages || 0), 0);
  const overallPct     = totalStages ? Math.round((totalCompleted / totalStages) * 100) : 0;

  // Gauge
  const gaugeOpt = {
    series: [{
      type: 'gauge', center: ['50%', '50%'], startAngle: 200, endAngle: -20, min: 0, max: 100, radius: '90%',
      pointer: { show: false },
      progress: { show: true, overlap: false, roundCap: true, clip: false,
        itemStyle: { color: overallPct >= 75 ? '#91cc75' : overallPct >= 40 ? '#fac858' : '#ee6666' } },
      axisLine: { lineStyle: { width: 20, color: [[1, '#f0f0f0']] } },
      splitLine: { show: false }, axisTick: { show: false }, axisLabel: { show: false },
      detail: { valueAnimation: true, offsetCenter: [0, 0], fontSize: 30, fontWeight: 700,
        formatter: '{value}%', color: overallPct >= 75 ? '#52c41a' : overallPct >= 40 ? '#fa8c16' : '#ff4d4f' },
      data: [{ value: overallPct, name: 'Complete' }],
      title: { offsetCenter: [0, '60%'], fontSize: 12, color: '#888' },
    }],
  };

  // Donut per assignment
  const donuts = [...assignments].sort((a, b) => new Date(a.exam_date || 0) - new Date(b.exam_date || 0)).map(a => {
    const pct = Math.round((a.completed_stages / Math.max(a.total_stages, 1)) * 100);
    const col = pct === 100 ? '#91cc75' : pct >= 60 ? '#5470c6' : pct >= 30 ? '#fac858' : '#ee6666';
    return {
      pct, col, a,
      opt: {
        tooltip: { show: false },
        series: [{
          type: 'pie', radius: ['55%', '80%'], center: ['50%', '50%'],
          label: { show: true, position: 'center', formatter: `{a|${pct}%}`, rich: { a: { fontSize: 18, fontWeight: 700, color: col } } },
          data: [
            { value: a.completed_stages, itemStyle: { color: col } },
            { value: Math.max(a.total_stages - a.completed_stages, 0), itemStyle: { color: '#f0f0f0' } },
          ],
          emphasis: { disabled: true },
        }],
      },
    };
  });

  // Stage status breakdown per assignment
  const assignmentNames = [...new Set(stages.map(s => s.template_title))];
  const statusGroups = {
    completed: { label: 'Completed', color: '#91cc75' },
    submitted: { label: 'Awaiting review', color: '#fac858' },
    active:    { label: 'In progress', color: '#5470c6' },
    locked:    { label: 'Locked', color: '#d9d9d9' },
    skipped:   { label: 'Skipped', color: '#f0f0f0' },
  };
  const statusOpt = assignmentNames.length === 0 ? null : {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    legend: { data: Object.values(statusGroups).map(g => g.label), bottom: 0, textStyle: { fontSize: 11 } },
    grid: { left: 130, right: 20, top: 10, bottom: 40 },
    xAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: '#f5f5f5' } } },
    yAxis: { type: 'category', data: assignmentNames, axisLabel: { fontSize: 11, width: 115, overflow: 'truncate' } },
    series: Object.entries(statusGroups).map(([status, { label, color }]) => ({
      name: label, type: 'bar', stack: 'total', barMaxWidth: 24,
      itemStyle: { color },
      data: assignmentNames.map(name => stages.filter(s => s.template_title === name && s.stage_status === status).length),
      label: {
        show: true,
        formatter: p => p.value > 0 ? p.value : '',
        fontSize: 10, color: status === 'locked' || status === 'skipped' ? '#aaa' : '#555',
      },
    })),
  };

  return (
    <>
      <Row gutter={[20, 20]} style={{ marginBottom: 20 }}>
        {/* Gauge */}
        <Col xs={24} sm={8}>
          <Card variant="outlined" style={{ borderColor: '#e8e8e8', textAlign: 'center' }}
            title={<span style={{ fontWeight: 600 }}>Overall Progress</span>}>
            <Chart option={gaugeOpt} height={210} />
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{totalCompleted} / {totalStages} stages completed</div>
          </Card>
        </Col>

        {/* Donut row */}
        <Col xs={24} sm={16}>
          <Card variant="outlined" style={{ borderColor: '#e8e8e8', height: '100%' }}
            title={<span style={{ fontWeight: 600 }}><AimOutlined style={{ marginRight: 8 }} />Progress by Assignment</span>}
            extra={<Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/stage-gate')}>View details →</Button>}>
            <Row gutter={[8, 8]} justify="center">
              {donuts.map(({ pct, col, a, opt }) => (
                <Col xs={12} sm={8} key={a.assignment_id} onClick={() => navigate('/stage-gate')} style={{ cursor: 'pointer' }}>
                  <Chart option={opt} height={120} />
                  <div style={{ textAlign: 'center', marginTop: -6 }}>
                    <div style={{ fontWeight: 600, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.template_title}</div>
                    <div style={{ fontSize: 10, color: '#aaa' }}>{a.tutor_name}</div>
                    {a.exam_date && (
                      <div style={{ fontSize: 10, color: dayjs(a.exam_date).diff(dayjs(), 'day') <= 14 ? '#fa8c16' : '#aaa' }}>
                        {dayjs(a.exam_date).diff(dayjs(), 'day')}d to exam
                      </div>
                    )}
                  </div>
                </Col>
              ))}
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Stage status breakdown */}
      {statusOpt && (
        <Card variant="outlined" style={{ borderColor: '#e8e8e8' }}
          title={<span style={{ fontWeight: 600 }}>Stage Status Breakdown</span>}>
          <Chart option={statusOpt} height={Math.max(120, assignmentNames.length * 48 + 60)} />
        </Card>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function Analytics() {
  const { user }  = useUser();
  const navigate  = useNavigate();
  const isTutor   = user.role === 'tutor' || user.role === 'admin';

  return (
    <AppLayout>
      <div className="page-header">
        <h1>Analytics</h1>
        <p>{isTutor ? 'Student performance and gate insights.' : 'Your learning progress and AI scores.'}</p>
      </div>
      {isTutor
        ? <TutorAnalytics user={user} navigate={navigate} />
        : <StudentAnalytics user={user} navigate={navigate} />
      }
    </AppLayout>
  );
}
